import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { MoreHorizontal, PencilIcon, PlusIcon, ShieldCheck, TrashIcon } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { usePermissions } from "@/lib/permissions";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { EmptyState, ErrorState, HeaderRow, LoadingState, TH, TableShell } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { ApprovalPolicyFormDialog } from "@/components/ApprovalPolicyFormDialog";
import {
  actionBadgeClass,
  actionLabel,
  conditionSummary,
  type ApprovalPolicyResponse,
} from "@/lib/approval-policy-utils";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type SupplierResponse = components["schemas"]["SupplierResponse"];
type OrganisationUserResponse = components["schemas"]["OrganisationUserResponse"];

const USER_FETCH_LIMIT = 100;
/** Approver names shown before collapsing to "+N". */
const MAX_APPROVERS_SHOWN = 3;

const SUPPLIER_FETCH_LIMIT = 200;
/** Max supplier pills shown inline in the "Applies to" cell before collapsing to "+N more". */
const MAX_SUPPLIER_PILLS = 4;

export interface ApprovalPoliciesPageProps {
  /** Optional link to where the old bill-reviewer settings used to live, for the migration callout. */
  legacySettingsLink?: ReactNode;
  onSupplierClick?: (supplierId: string) => void;
  /** Optional pluggable Link component; only used for supplier pills if onSupplierClick is not provided. */
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
}

/** Empty selection is a global policy ("All suppliers"); an unresolved id shows shortened so the pill is never blank. */
function AppliesToCell({
  supplierIds,
  nameFor,
  onSupplierClick,
  LinkComponent,
}: {
  supplierIds: string[];
  nameFor: (id: string) => string;
  onSupplierClick?: (supplierId: string) => void;
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
}) {
  if (supplierIds.length === 0) {
    return (
      <Badge variant="secondary" className="font-normal">
        All suppliers
      </Badge>
    );
  }

  const shown = supplierIds.slice(0, MAX_SUPPLIER_PILLS);
  const overflow = supplierIds.length - shown.length;

  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((id) => {
        const name = nameFor(id);
        if (onSupplierClick) {
          return (
            <Badge
              key={id}
              variant="outline"
              asChild
              className="cursor-pointer font-normal hover:bg-muted"
            >
              <button type="button" onClick={() => onSupplierClick(id)}>
                {name}
              </button>
            </Badge>
          );
        }
        if (LinkComponent) {
          return (
            <Badge key={id} variant="outline" asChild className="font-normal hover:bg-muted">
              <LinkComponent href={`/suppliers/${id}`}>{name}</LinkComponent>
            </Badge>
          );
        }
        return (
          <Badge key={id} variant="outline" className="font-normal">
            {name}
          </Badge>
        );
      })}
      {overflow > 0 && (
        <Badge variant="secondary" className="font-normal text-muted-foreground">
          +{overflow} more
        </Badge>
      )}
    </div>
  );
}

/** Only review policies have approvers; auto-approve and auto-void decide on their own. */
function ApproversCell({
  policy,
  userFor,
}: {
  policy: ApprovalPolicyResponse;
  userFor: (id: string) => OrganisationUserResponse | undefined;
}) {
  if (policy.action !== "AlwaysReview") {
    return <span className="text-xs text-muted-foreground">No one, decided automatically</span>;
  }
  const ids = policy.approverUserIds ?? [];
  if (ids.length === 0) {
    return <span className="text-xs text-muted-foreground">Any admin</span>;
  }
  const people = ids.map((id) => {
    const u = userFor(id);
    return { id, name: u?.name || u?.email || "Former user" };
  });
  const shown = people.slice(0, MAX_APPROVERS_SHOWN);
  const overflow = people.length - shown.length;
  return (
    <span className="block truncate text-sm">
      {shown.map((p) => p.name).join(", ")}
      {overflow > 0 ? <span className="text-muted-foreground">{` +${overflow}`}</span> : null}
    </span>
  );
}

/** Mutations are gated by `manageApprovalPolicies`; the backend 403 is the real enforcement. */
export function ApprovalPoliciesPage({ onSupplierClick, LinkComponent }: ApprovalPoliciesPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canManage = can("manageApprovalPolicies");

  const [policies, setPolicies] = useState<ApprovalPolicyResponse[]>([]);
  const [supplierNames, setSupplierNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ApprovalPolicyResponse | null>(null);
  const [policyToDelete, setPolicyToDelete] = useState<ApprovalPolicyResponse | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [users, setUsers] = useState<Record<string, OrganisationUserResponse>>({});

  async function fetchPolicies() {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await api.GET("/v1/ap/approval-policies");
    if (apiError) {
      setError(apiErrorMessage(apiError, "Couldn't load your approval policies."));
    } else {
      setPolicies(data ?? []);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve supplier ids → names for the "Applies to" pills. Best-effort: on
  // failure pills fall back to a shortened id (see nameFor below).
  useEffect(() => {
    let cancelled = false;
    api
      .GET("/v1/suppliers", { params: { query: { Limit: SUPPLIER_FETCH_LIMIT } } })
      .then(({ data }) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const s of data?.data ?? ([] as SupplierResponse[])) {
          if (s.id) map[s.id] = s.name;
        }
        setSupplierNames(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Resolve approver ids → people, so a policy says who it routes to rather than how many. Best-effort
  // like the supplier names above.
  useEffect(() => {
    let cancelled = false;
    api
      .GET("/v1/users", { params: { query: { Limit: USER_FETCH_LIMIT } } })
      .then(({ data }) => {
        if (cancelled) return;
        const map: Record<string, OrganisationUserResponse> = {};
        for (const u of data?.data ?? []) {
          if (u.id) map[u.id] = u;
        }
        setUsers(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api]);

  const nameFor = (id: string) => supplierNames[id] ?? `${id.slice(0, 8)}…`;

  async function confirmDelete() {
    const target = policyToDelete;
    if (!target) return;
    setPolicyToDelete(null);
    const { error: apiError } = await api.DELETE("/v1/ap/approval-policies/{id}", {
      params: { path: { id: target.id } },
    });
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to delete approval policy"));
      return;
    }
    toast.success("Approval policy deleted");
    setPolicies((prev) => prev.filter((p) => p.id !== target.id));
  }

  // Full-update PUT to flip `active` — the upsert body is the whole policy.
  async function toggleActive(policy: ApprovalPolicyResponse, next: boolean) {
    setTogglingId(policy.id);
    const { data, error: apiError } = await api.PUT("/v1/ap/approval-policies/{id}", {
      params: { path: { id: policy.id } },
      body: {
        name: policy.name,
        priorityOrder: policy.priorityOrder,
        active: next,
        amountGte: policy.amountGte ?? null,
        amountLte: policy.amountLte ?? null,
        currency: policy.currency ?? null,
        supplierIds: policy.supplierIds,
        supplierStanding: policy.supplierStanding,
        action: policy.action,
        approverUserIds: policy.approverUserIds,
      },
    });
    setTogglingId(null);
    if (apiError || !data) {
      toast.error(apiErrorMessage(apiError, "Failed to update approval policy"));
      return;
    }
    setPolicies((prev) => prev.map((p) => (p.id === data.id ? data : p)));
  }

  // Evaluation order, so the table reads top to bottom the way a bill is checked against it.
  const ordered = [...policies].sort((a, b) => Number(a.priorityOrder) - Number(b.priorityOrder));

  const nextPriority = policies.length
    ? Math.min(1000, Math.max(...policies.map((p) => Number(p.priorityOrder))) + 10)
    : 100;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Payables"
        title="Approval policies"
        subtitle="Rules that decide what happens when a bill is submitted for review - auto-approve, route for approval, or auto-void."
        actions={
          canManage ? (
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              className="w-full gap-1.5 sm:w-auto"
            >
              <PlusIcon className="size-4" />
              New policy
            </Button>
          ) : undefined
        }
      />

      {loading ? (
        <LoadingState label="Loading approval policies..." />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchPolicies} />
      ) : policies.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No approval policies yet"
          action={
            canManage ? (
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                <PlusIcon className="size-4" /> New policy
              </Button>
            ) : undefined
          }
        >
          Every bill submitted for review goes to an admin. Add a policy to auto-approve small or trusted
          spend, or to name who approves what.
        </EmptyState>
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={`w-[84px] ${TH}`}>
                    <span className="inline-flex items-center gap-1">
                      Priority
                      <InfoHint label="priority">
                        A submitted bill is checked against active policies from the top. The first one it
                        matches decides the outcome; lower numbers are checked first.
                      </InfoHint>
                    </span>
                  </TableHead>
                  <TableHead className={TH}>Name</TableHead>
                  <TableHead className={TH}>Conditions</TableHead>
                  <TableHead className={TH}>Applies to</TableHead>
                  <TableHead className={`w-[120px] ${TH}`}>Action</TableHead>
                  <TableHead className={TH}>Approvers</TableHead>
                  <TableHead className={`w-[90px] ${TH}`}>Active</TableHead>
                  <TableHead className="w-[48px]" />
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {ordered.map((p) => (
                  <TableRow key={p.id} className={p.active ? undefined : "opacity-60"}>
                    <TableCell className="font-mono text-xs text-muted-foreground tabular-nums">
                      {p.priorityOrder}
                    </TableCell>
                    <TableCell className="font-semibold">{p.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{conditionSummary(p)}</TableCell>
                    <TableCell>
                      <AppliesToCell
                        supplierIds={p.supplierIds}
                        nameFor={nameFor}
                        onSupplierClick={onSupplierClick}
                        LinkComponent={LinkComponent}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs font-normal ${actionBadgeClass(p.action)}`}>
                        {actionLabel(p.action)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <ApproversCell policy={p} userFor={(id) => users[id]} />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={p.active}
                        disabled={!canManage || togglingId === p.id}
                        onCheckedChange={(next) => toggleActive(p, next)}
                        aria-label={`Toggle ${p.name} active`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {canManage ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="size-8" aria-label={`Actions for ${p.name}`}>
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditing(p);
                                setFormOpen(true);
                              }}
                            >
                              <PencilIcon className="mr-2 size-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setPolicyToDelete(p)}
                            >
                              <TrashIcon className="mr-2 size-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
          {/* The fall-through is the rule people forget exists, so it is stated where the list ends. */}
          <p className="pt-3 text-xs text-muted-foreground">
            A bill that matches none of the active policies is routed to an admin for review.
          </p>
        </>
      )}

      <ApprovalPolicyFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
        policy={editing}
        defaultPriority={nextPriority}
        onSaved={fetchPolicies}
      />

      <AlertDialog
        open={policyToDelete !== null}
        onOpenChange={(open) => !open && setPolicyToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete approval policy?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{policyToDelete?.name}&rdquo; will be permanently removed. Bills submitted
              afterwards will be evaluated against the remaining policies.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
